/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 */

import React, { useState } from 'react';
import _ from 'lodash';
import { Icon, Dropdown } from 'semantic-ui-react';

const iconStyle = { lineHeight: 1, height: '1em', margin: 0 };

// Mode toggle -- AND / OR
function ModeToggle({ mode, onToggle }) {
  return (
    <div style={{ display: 'flex', gap: '4px', marginBottom: '14px' }}>
      <button
        className={`workspace-action-btn ${mode === 'and' ? 'workspace-action-btn-primary' : 'workspace-action-btn-secondary'}`}
        onClick={() => onToggle('and')}
        style={{ padding: '6px 14px', fontSize: '0.8em' }}
      >
        {mode === 'and' && <Icon name="check" style={iconStyle} />} AND
      </button>
      <button
        className={`workspace-action-btn ${mode === 'or' ? 'workspace-action-btn-primary' : 'workspace-action-btn-secondary'}`}
        onClick={() => onToggle('or')}
        style={{ padding: '6px 14px', fontSize: '0.8em' }}
      >
        {mode === 'or' && <Icon name="check" style={iconStyle} />} OR
      </button>
    </div>
  );
}

// expected props
// - mode, filters, fields, onFilter
export default function FilterForm({ mode, filters, fields, onFilter }) {
  const [form, setForm] = useState(
    filters.reduce((acc, { key, value, match }) => ({ ...acc, [key]: { value: [...value], match } }), {
      id: { value: [] },
      name: { value: [] },
      user: { value: [] },
      project: { value: [] },
      study: { value: [] },
      type: { value: [] },
      configType: { value: [] },
      status: { value: [] },
    }),
  );

  const iconsMap = _.reduce(fields, (acc, { key, icon }) => ({ ...acc, [key]: icon }), {});

  const getFiltersFromForm = formValues =>
    Object.entries(formValues)
      .filter(([, { value }]) => value.length > 1 || value[0])
      .map(([key, { value, match = 'exact' }]) => ({ key, value, icon: iconsMap[key], match }));

  function handleChange(key, match) {
    return (e, { value }) => {
      const newForm = { ...form, [key]: { value: Array.isArray(value) ? value : [value], match } };
      onFilter({ filters: getFiltersFromForm(newForm), mode });
      setForm(newForm);
    };
  }

  function handleTextChange(key) {
    return (e) => {
      const value = e.target.value;
      const newForm = { ...form, [key]: { value: [value], match: 'partial' } };
      onFilter({ filters: getFiltersFromForm(newForm), mode });
      setForm(newForm);
    };
  }

  function updateMode(newMode) {
    const newForm = _.mapValues(form, ({ value }) => ({ value: value.slice(0, 1) }));
    onFilter({ filters: getFiltersFromForm(newForm), mode: newMode });
    setForm(newForm);
  }

  const filterableFields = fields.filter(({ filterable = true }) => filterable);

  return (
    <div>
      <ModeToggle mode={mode} onToggle={updateMode} />
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
        {filterableFields.map(({ key, icon, label, options }) => (
          <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '180px', flex: '1 1 180px', maxWidth: '280px' }}>
            <label style={{ fontSize: '0.75em', fontWeight: 600, color: '#6b7280', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Icon name={icon} style={{ ...iconStyle, fontSize: '0.9em' }} />
              {label}
            </label>
            {options ? (
              <Dropdown
                selection
                search
                multiple={mode === 'or'}
                options={options}
                value={mode === 'or' ? form[key].value : form[key].value[0]}
                onChange={handleChange(key, mode === 'or' ? 'multiple' : 'exact')}
                placeholder={`Filter by ${label.toLowerCase()}...`}
                style={{ fontSize: '0.85em', borderRadius: '8px', minHeight: '36px' }}
                clearable
              />
            ) : (
              <input
                type="text"
                value={form[key].value[0] || ''}
                onChange={handleTextChange(key)}
                placeholder={`Filter by ${label.toLowerCase()}...`}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '0.85em',
                  fontFamily: 'inherit',
                  outline: 'none',
                  transition: 'border-color 0.15s ease',
                }}
                onFocus={e => { e.target.style.borderColor = 'var(--rmit-blue-50)'; }}
                onBlur={e => { e.target.style.borderColor = '#e5e7eb'; }}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
